import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButton,
} from "../ModalShared";
import {
  FiUploadCloud,
  FiPackage,
  FiSearch,
  FiDatabase,
  FiCheckCircle,
  FiAlertCircle,
  FiLoader,
  FiClock,
  FiWifi,
  FiWifiOff,
  FiX,
  FiMinimize2,
  FiTerminal,
} from "react-icons/fi";
import { GoSync, GoCheck, GoX } from "react-icons/go";
import authService from "../../../../services/authService";
import WorkerLogTerminal from "../../../../components/WorkerLogTerminal";
import { Container, Header, TaskList, TaskItem, StatusMessage } from "./styles";

const WS_URL = process.env.REACT_APP_WS_URL || "wss://log.vidal-app.com/ws";

const STAGES = [
  { id: "upload", name: "Upload dos arquivos", icon: FiUploadCloud },
  { id: "decompress", name: "Descompactação", icon: FiPackage },
  { id: "classify", name: "Classificação", icon: FiSearch },
  { id: "process", name: "Processamento", icon: FiDatabase },
  { id: "complete", name: "Conclusão", icon: FiCheckCircle },
];

function mapStageToIndex(stageText) {
  if (!stageText) return -1;
  const lower = stageText.toLowerCase();
  if (lower.includes("descompact")) return 1;
  if (lower.includes("classific")) return 2;
  if (lower.includes("process") || lower.includes("xml") || lower.includes("sped")) return 3;
  if (lower.includes("conclu") || lower.includes("notific")) return 4;
  return 0;
}

export default function UploadProgressModal({
  projectId,
  operationId,
  uploadProgress = 100,
  onComplete,
  onMinimize,
  onCancel,
}) {
  const [globalProgress, setGlobalProgress] = useState(uploadProgress >= 100 ? 5 : 0);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [statusMessage, setStatusMessage] = useState("Conectando ao servidor...");
  const [overallStatus, setOverallStatus] = useState("processing");
  const [wsConnected, setWsConnected] = useState(false);
  const [stats, setStats] = useState({});
  const [showTerminal, setShowTerminal] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  const handleMessage = useCallback(
    (rawData) => {
      const data = rawData.message || rawData;
      if (data.type !== "smart_upload_progress") return;
      if (data.projectId !== projectId) return;

      const payload = data.data || {};
      const { stage, progress, status, stats: newStats } = payload;

      if (stage) {
        setStatusMessage(stage);
        const idx = mapStageToIndex(stage);
        if (idx >= 0) setCurrentStageIndex(idx);
      }

      if (progress !== undefined) {
        setGlobalProgress(Math.round(progress));
      }

      if (newStats) {
        setStats(newStats);
      }

      if (status === "completed") {
        setOverallStatus("success");
        setGlobalProgress(100);
        setCurrentStageIndex(4);
        setTimeout(() => onComplete && onComplete(), 3000);
      } else if (status === "error") {
        setOverallStatus("error");
      }
    },
    [projectId, onComplete]
  );

  useEffect(() => {
    const connect = () => {
      const token = authService.getToken();
      if (!token) return;

      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        setStatusMessage("Aguardando atualizações...");

        ws.send(
          JSON.stringify({
            action: "subscribe",
            type: "smart_upload_progress",
            projectId,
          })
        );

        // Also subscribe to process_upload_xmls and sped processing for delegation
        ["process_upload_xmls", "unzip_upload", "process_sped_icms", "process_sped_pis"].forEach((type) => {
          ws.send(JSON.stringify({ action: "subscribe", type, projectId }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.success && parsed.message === "Inscrição realizada com sucesso") return;
          if (parsed.message?.type === "info") return;
          handleMessage(parsed);
        } catch (e) {
          // Ignore parse errors
        }
      };

      ws.onerror = () => setWsConnected(false);
      ws.onclose = () => {
        setWsConnected(false);
        if (overallStatus === "processing") {
          reconnectRef.current = setTimeout(connect, 3000);
        }
      };
    };

    connect();

    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [projectId, handleMessage, overallStatus]);

  // Mark upload stage as done immediately since we open after upload completes
  useEffect(() => {
    if (uploadProgress >= 100) {
      setCurrentStageIndex((prev) => Math.max(prev, 1));
      setGlobalProgress((prev) => Math.max(prev, 5));
    }
  }, [uploadProgress]);

  const getTaskStatus = (idx) => {
    if (overallStatus === "success") return "completed";
    if (overallStatus === "error" && idx === currentStageIndex) return "error";
    if (idx < currentStageIndex) return "completed";
    if (idx === currentStageIndex) return "active";
    return "pending";
  };

  const fmt = (n) => (n || 0).toLocaleString("pt-BR");

  return (
    <ModalOverlay>
      <ModalContainer $maxWidth="500px" onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-left">
            <div className="header-icon">
              <GoSync className={overallStatus === "processing" ? "spin" : ""} />
            </div>
            <div>
              <h2>Upload Inteligente</h2>
              <p>Processamento automático de arquivos</p>
            </div>
          </div>
          <div className="header-right" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              title="Ver logs do worker em tempo real"
              onClick={() => setShowTerminal(true)}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#8b949e",
                padding: "4px 8px",
                borderRadius: 6,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "0.72em",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = "#10b981"; e.currentTarget.style.borderColor = "#10b98155"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = "#8b949e"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"; }}
            >
              <FiTerminal /> logs
            </button>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: "0.75em",
                color: wsConnected ? "#10b981" : "#ef4444",
                opacity: 0.7,
              }}
            >
              {wsConnected ? <FiWifi /> : <FiWifiOff />}
            </span>
          </div>
        </ModalHeader>

        <ModalBody>
          <Container>
            <Header>
              <div className="global-progress" style={{ flex: 1 }}>
                <div className="progress-text">
                  <span>{globalProgress}%</span> concluído
                  {stats.xmlsFound > 0 && <small> &bull; {fmt(stats.xmlsFound)} XMLs</small>}
                  {stats.spedsFound > 0 && <small> &bull; {fmt(stats.spedsFound)} SPEDs</small>}
                </div>
                <div className="progress-bar-container" style={{ width: "100%" }}>
                  <div className="progress-fill" style={{ width: `${globalProgress}%` }} />
                </div>
              </div>
            </Header>

            <TaskList>
              {STAGES.map((stage, idx) => {
                const status = getTaskStatus(idx);
                const Icon = stage.icon;
                return (
                  <TaskItem key={stage.id} className={status}>
                    <div className={`task-icon ${status}`}>
                      {status === "pending" && <FiClock />}
                      {status === "active" && <FiLoader className="spin" />}
                      {status === "completed" && <FiCheckCircle />}
                      {status === "error" && <FiAlertCircle />}
                    </div>
                    <div className="task-info">
                      <div className="task-header">
                        <span className="task-name">{stage.name}</span>
                        <span className={`task-status ${status}`}>
                          {status === "pending" && "Aguardando"}
                          {status === "active" && "Em andamento..."}
                          {status === "completed" && "Concluído"}
                          {status === "error" && "Erro"}
                        </span>
                      </div>
                      {status === "active" && (
                        <div className="task-progress">
                          <div className="progress-fill active" style={{ width: "60%" }} />
                        </div>
                      )}
                      {status === "completed" && (
                        <div className="task-progress">
                          <div className="progress-fill completed" style={{ width: "100%" }} />
                        </div>
                      )}
                    </div>
                  </TaskItem>
                );
              })}
            </TaskList>

            {/* Stats summary */}
            {(stats.xmlsFound > 0 || stats.spedsFound > 0 || stats.others > 0) && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                gap: "8px",
                marginTop: "8px",
              }}>
                {stats.xmlsFound > 0 && (
                  <StatBadge label="XMLs" value={stats.xmlsProcessed || stats.xmlsFound} color="#10b981" />
                )}
                {stats.spedsFound > 0 && (
                  <StatBadge label="SPEDs" value={stats.spedsProcessed || stats.spedsFound} color="#6366f1" />
                )}
                {stats.others > 0 && (
                  <StatBadge label="Outros" value={stats.others} color="#f59e0b" />
                )}
                {stats.errors?.length > 0 && (
                  <StatBadge label="Erros" value={stats.errors.length} color="#ef4444" />
                )}
              </div>
            )}

            {overallStatus === "success" && (
              <StatusMessage className="success">
                <div className="status-icon success">
                  <GoCheck />
                </div>
                <div className="status-text">
                  <h4>Upload Processado!</h4>
                  <p>{statusMessage}</p>
                </div>
                <div className="status-action">
                  <button onClick={onComplete}>Ver Arquivos</button>
                </div>
              </StatusMessage>
            )}

            {overallStatus === "error" && (
              <StatusMessage className="error">
                <div className="status-icon error">
                  <GoX />
                </div>
                <div className="status-text">
                  <h4>Erro no Processamento</h4>
                  <p>{statusMessage}</p>
                </div>
              </StatusMessage>
            )}
          </Container>
        </ModalBody>

        <ModalFooter $justify="space-between">
          <ModalButton className="danger" onClick={onCancel}>
            <FiX /> Cancelar
          </ModalButton>
          {onMinimize && (
            <ModalButton className="secondary" onClick={onMinimize}>
              <FiMinimize2 /> Minimizar
            </ModalButton>
          )}
        </ModalFooter>
      </ModalContainer>

      {showTerminal && (
        <WorkerLogTerminal
          projectId={projectId}
          onClose={() => setShowTerminal(false)}
        />
      )}
    </ModalOverlay>
  );
}

function StatBadge({ label, value, color }) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: "8px",
        padding: "8px 12px",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "1.1em", fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: "0.7em", opacity: 0.7, color }}>{label}</div>
    </div>
  );
}
